import React, { useEffect, useState, useCallback } from 'react';
import { Truck, MapPin, AlertTriangle, FileText, FileSpreadsheet, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { inv, INV_API } from '../../lib/invApi';

const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

// Shared shortage-driven restock: proposes a Shop→destination transfer using available
// source stock first; leftover stays a purchasing shortage. Committing runs the canonical
// transfer engine (moves stock + ledger). Never changes Target/Par.
const RestockDialog = ({ open, onOpenChange, locations, toLocationId, toLocationName, templateId, templateName, onDone }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sourceId, setSourceId] = useState('');
  const [destId, setDestId] = useState(toLocationId || '');
  const [qty, setQty] = useState({});
  const [committed, setCommitted] = useState(false);

  const sourceLocs = (locations || []).filter((l) => l.archived !== true && ['shop', 'warehouse'].includes(l.type));
  const destLocs = (locations || []).filter((l) => l.archived !== true && l.id !== sourceId);
  const effectiveDest = toLocationId || destId;

  const runPlan = useCallback(async (src, dst) => {
    if (!dst) return;
    setLoading(true); setCommitted(false);
    try {
      const body = { to_location_id: dst };
      if (src) body.from_location_id = src;
      if (templateId) body.template_id = templateId;
      const { data } = await inv.post('/restock/plan', body);
      setPlan(data);
      setSourceId(data.from_location.id);
      const q = {};
      data.lines.forEach((l) => { q[l.item_id] = l.transfer_qty; });
      setQty(q);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not build restock plan'); setPlan(null); }
    setLoading(false);
  }, [templateId]);

  useEffect(() => {
    if (open) { setPlan(null); setCommitted(false); setDestId(toLocationId || ''); setSourceId('');
      if (toLocationId) runPlan('', toLocationId); }
  }, [open, toLocationId, runPlan]);

  const onSource = (v) => { setSourceId(v); runPlan(v, effectiveDest); };
  const onDest = (v) => { setDestId(v); runPlan(sourceId, v); };

  const setQtyClamped = (l, v) => {
    let n = v === '' ? 0 : parseFloat(v);
    if (isNaN(n) || n < 0) n = 0;
    if (n > l.shop_available) n = l.shop_available;
    setQty((q) => ({ ...q, [l.item_id]: n }));
  };

  const totalTransfer = plan ? plan.lines.reduce((s, l) => s + (parseFloat(qty[l.item_id]) || 0), 0) : 0;

  const commit = async () => {
    const lines = plan.lines.map((l) => ({ item_id: l.item_id, transfer_qty: parseFloat(qty[l.item_id]) || 0 })).filter((l) => l.transfer_qty > 0);
    if (lines.length === 0) { toast.error('Nothing to transfer'); return; }
    try {
      const { data } = await inv.post('/restock/commit', { from_location_id: plan.from_location.id, to_location_id: plan.to_location.id, lines, notes: templateName ? `Restock: ${templateName}` : 'Restock from shortage' });
      toast.success(`Restock complete — ${data.lines} line(s) moved`);
      setCommitted(true);
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Restock failed'); }
  };

  const purchaseExportUrl = (fmt) => {
    if (templateId) return `${INV_API}/loadout-templates/${templateId}/export?location_id=${plan.to_location.id}&format=${fmt}`;
    return `${INV_API}/locations/${plan.to_location.id}/shortage-export?format=${fmt}`;
  };
  const download = (fmt) => window.open(purchaseExportUrl(fmt), '_blank');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inv-restock-dialog" className="bg-[#141414] border-[#262626] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-[#D4AF37]" /> Restock {toLocationName ? `— ${toLocationName}` : 'From Shortage'}</DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-xs">Plans a Shop → Van transfer toward the loadout target. Anything the Shop can't cover is shown as not available in Shop — company purchasing is decided by the Reorder List, not van differences. Confirming moves stock immediately and records the movement ledger. Target/Par is never changed.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap text-sm">
          <Label className="text-xs text-[#A3A3A3]">From</Label>
          <select data-testid="inv-restock-source" value={sourceId} onChange={(e) => onSource(e.target.value)} className="h-9 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2 min-w-[160px]">
            <option value="">First Shop</option>
            {sourceLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <ArrowRight className="w-4 h-4 text-[#525252]" />
          {toLocationId ? (
            <span className="flex items-center gap-1 text-white"><MapPin className="w-4 h-4 text-[#D4AF37]" />{toLocationName}</span>
          ) : (
            <select data-testid="inv-restock-dest" value={destId} onChange={(e) => onDest(e.target.value)} className="h-9 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2 min-w-[180px]">
              <option value="">Select destination…</option>
              {destLocs.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.type})</option>)}
            </select>
          )}
        </div>

        {loading && <p className="text-[#A3A3A3] py-6 text-center">Building restock plan…</p>}

        {plan && !loading && (
          plan.lines.length === 0 ? (
            <p data-testid="inv-restock-nothing" className="text-[#525252] py-6 text-center">No shortages to restock at this location.</p>
          ) : (
            <div className="space-y-4">
              <div className="border border-[#262626] rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0A0A] border-b border-[#262626]">
                    <tr>
                      <th className="text-left px-3 py-2 text-[#A3A3A3]">Item</th>
                      <th className="text-right px-3 py-2 text-[#A3A3A3]">Needed</th>
                      <th className="text-right px-3 py-2 text-[#A3A3A3]">Shop Avail</th>
                      <th className="text-right px-3 py-2 text-[#A3A3A3]">Transfer Qty</th>
                      <th className="text-right px-3 py-2 text-[#A3A3A3]">Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.lines.map((l) => {
                      const tq = parseFloat(qty[l.item_id]) || 0;
                      const remaining = Math.max(l.needed - tq, 0);
                      return (
                        <tr key={l.item_id} data-testid={`inv-restock-row-${l.item_id}`} className="border-b border-[#262626] last:border-0">
                          <td className="px-3 py-2 text-white">{l.name}<span className="text-[#525252] text-xs ml-2">{l.preferred_supplier}</span></td>
                          <td className="px-3 py-2 text-right text-[#A3A3A3]">{l.needed}</td>
                          <td className="px-3 py-2 text-right text-[#A3A3A3]">{l.shop_available}</td>
                          <td className="px-3 py-2 text-right">
                            <Input data-testid={`inv-restock-qty-${l.item_id}`} type="number" step="any" min="0" max={l.shop_available} value={qty[l.item_id]} disabled={committed} onChange={(e) => setQtyClamped(l, e.target.value)} className="w-20 h-8 bg-[#0A0A0A] border-[#262626] text-white text-sm text-right inline-block" />
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold ${remaining > 0 ? 'text-amber-400' : 'text-green-400'}`}>{remaining > 0 ? <><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />{remaining} buy</> : '✓'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {!committed ? (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-[#A3A3A3] text-xs">Transferring <span className="text-white font-semibold">{totalTransfer}</span> unit(s) · Not available in Shop: <span className="text-amber-400 font-semibold">{plan.totals.total_needs_purchase}</span></p>
                  <Button data-testid="inv-restock-commit" onClick={commit} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Truck className="w-4 h-4 mr-2" /> Confirm Transfer</Button>
                </div>
              ) : (
                <div className="bg-green-900/10 border border-green-800/40 rounded-md p-3 space-y-3">
                  <p className="text-green-400 text-sm font-medium">Transfer completed — stock moved and logged. Target/Par unchanged.</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#A3A3A3] text-xs">Order what the Shop couldn't cover:</span>
                    <Button data-testid="inv-restock-export-pdf" onClick={() => download('pdf')} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><FileText className="w-4 h-4 mr-1" /> Remaining PDF</Button>
                    <Button data-testid="inv-restock-export-csv" onClick={() => download('csv')} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><FileSpreadsheet className="w-4 h-4 mr-1" /> Remaining CSV</Button>
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RestockDialog;
